// Chart generation utilities shared across market screens
export interface ChartDimensions {
  width: number;
  height: { min: number; max: number };
}

export interface PriceDataPoint {
  price: number;
  timestamp: number;
}

export interface PriceRange {
  min: number;
  max: number;
}

export interface ChartPosition {
  x: number;
  y: number;
}

export const DEFAULT_CHART_DIMENSIONS: ChartDimensions = {
  width: 360, // This will be overridden by dynamic width
  height: { min: 80, max: 340 },
};

/**
 * Fills gaps in price history data with the previous price value.
 * Inserts data points at 1-second intervals for gaps larger than 2 seconds.
 */
export const fillPriceHistoryGaps = (
  priceHistory: PriceDataPoint[],
  maxGapMs = 2000,
  intervalMs = 1000,
): PriceDataPoint[] => {
  if (priceHistory.length <= 1) {
    return priceHistory;
  }

  const filledData: PriceDataPoint[] = [];

  for (let i = 0; i < priceHistory.length; i++) {
    filledData.push(priceHistory[i]);

    // Fill gaps between current and next point
    if (i < priceHistory.length - 1) {
      const currentPoint = priceHistory[i];
      const nextPoint = priceHistory[i + 1];
      const timeDiff = nextPoint.timestamp - currentPoint.timestamp;

      // Fill gaps larger than maxGapMs with intervalMs intervals
      if (timeDiff > maxGapMs) {
        for (
          let t = currentPoint.timestamp + intervalMs;
          t < nextPoint.timestamp;
          t += intervalMs
        ) {
          filledData.push({
            timestamp: t,
            price: currentPoint.price, // Use previous price for gap
          });
        }
      }
    }
  }

  return filledData;
};

export const generateChartPriceRange = (
  priceHistory: { price: number }[],
  marginPercentage = 5,
  strikePrice?: number,
): PriceRange | null => {
  if (priceHistory.length < 10) {
    return null;
  }

  const prices = priceHistory.slice(-30).map(p => p.price);
  let min = Math.min(...prices);
  let max = Math.max(...prices);

  // Include strike price in the range calculation if provided
  if (strikePrice && strikePrice > 0) {
    min = Math.min(min, strikePrice);
    max = Math.max(max, strikePrice);
  }

  // Apply margin to create some visual breathing room
  const range = max - min;
  const margin = range * (marginPercentage / 100);

  return {
    min: min - margin,
    max: max + margin,
  };
};

export const generateSVGPath = (
  priceHistory: { price: number }[],
  priceRange: PriceRange,
  dimensions: ChartDimensions = DEFAULT_CHART_DIMENSIONS,
  dynamicWidth?: number,
): string | null => {
  if (priceHistory.length < 2) {
    return null;
  }

  const prices = priceHistory.slice(-30).map(p => p.price);
  let pathData = '';
  const effectiveWidth = dynamicWidth ?? dimensions.width;
  const xStep = effectiveWidth / Math.max(prices.length - 1, 1);

  for (const [i, price] of prices.entries()) {
    const x = i * xStep;
    const priceRatio =
      (price - priceRange.min) / (priceRange.max - priceRange.min);
    const y =
      dimensions.height.max -
      priceRatio * (dimensions.height.max - dimensions.height.min);
    const finalY = Math.max(
      dimensions.height.min,
      Math.min(dimensions.height.max, y),
    );

    if (i === 0) {
      pathData = `M${x} ${finalY}`;
    } else {
      const prevX = (i - 1) * xStep;
      const controlX1 = prevX + xStep * 0.4;
      const controlX2 = x - xStep * 0.4;
      pathData += ` C${controlX1} ${pathData.split(' ').pop()} ${controlX2} ${finalY} ${x} ${finalY}`;
    }
  }

  return pathData;
};

export const calculatePricePosition = (
  price: number,
  priceRange: PriceRange,
  dimensions: ChartDimensions = DEFAULT_CHART_DIMENSIONS,
  dynamicWidth?: number,
): ChartPosition | null => {
  if (price === 0) {
    return null;
  }

  const priceRatio =
    (price - priceRange.min) / (priceRange.max - priceRange.min);
  const y =
    dimensions.height.max -
    priceRatio * (dimensions.height.max - dimensions.height.min);
  const finalY = Math.max(
    dimensions.height.min,
    Math.min(dimensions.height.max, y),
  );

  const effectiveWidth = dynamicWidth ?? dimensions.width;
  return { x: effectiveWidth, y: finalY };
};

export const formatPrice = (price: number): string => {
  if (price === 0) {
    return '---';
  }
  return `${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatStrikePrice = (price: number): string => {
  if (price === 0) {
    return '---';
  }

  // Determine decimal places dynamically based on price size
  let fractionDigits = 0;

  if (price < 1) {
    fractionDigits = 4;
  } else if (price < 10) {
    fractionDigits = 3;
  } else if (price < 20) {
    fractionDigits = 2;
  } else if (price < 100) {
    fractionDigits = 1;
  } else {
    fractionDigits = 0;
  }

  return `$${price.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
};

export const formatTime = (time: {
  hours: number;
  minutes: number;
  seconds: number;
}): string => {
  const h = time.hours.toString().padStart(1, '0');
  const m = time.minutes.toString().padStart(2, '0');
  const s = time.seconds.toString().padStart(2, '0');
  return h === '0'
    ? m === '0'
      ? `${s}s`
      : `${m}m ${s}s`
    : `${h}h ${m}m ${s}s`;
};

// Strike price calculation utilities
export const roundToNearestMultiple = (
  value: number,
  multiple: number,
): number => {
  return Math.round(value / multiple) * multiple;
};

export const getAssetStrikeMultiple = (asset: string): number => {
  switch (asset.toUpperCase()) {
    case 'BTC':
      return 1000;
    case 'ETH':
      return 100;
    case 'SOL':
      return 1;
      // return 0.5;
    case 'XRP':
      return 0.01;
    default:
      return 25;
  }
};

export const generateDynamicStrikePrices = (
  spotPrice: number,
  asset: string,
): number[] => {
  const multiple = getAssetStrikeMultiple(asset);
  const roundedPrice = roundToNearestMultiple(spotPrice, multiple);
  const prices: number[] = [];

  // Create array from high to low (iOS style - higher numbers at top)
  for (let i = 10; i >= -10; i--) {
    const price = roundedPrice + i * multiple;
    // For XRP, ensure proper decimal precision
    if (asset.toUpperCase() === 'XRP') {
      prices.push(+price.toFixed(2));
    } else {
      prices.push(price);
    }
  }

  return prices;
};

export const findNearestStrikeIndex = (
  spotPrice: number,
  strikePrices: number[],
): number => {
  if (spotPrice === 0 || strikePrices.length === 0) {
    return 10; // Default to middle index
  }

  let closestIndex = 0;
  let smallestDifference = Math.abs(strikePrices[0] - spotPrice);

  for (let i = 1; i < strikePrices.length; i++) {
    const difference = Math.abs(strikePrices[i] - spotPrice);
    if (difference < smallestDifference) {
      smallestDifference = difference;
      closestIndex = i;
    }
  }

  return closestIndex;
};
